import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { selectAuth } from "../features/authSlice";
import { RootState } from "../store";
import { baseUrl } from './baseUrl';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl.MAIN_BASE_URL,
})

type ExtraOptions = {
    headers?: Record<string, string>;
    // Define other optional properties here if needed
};

const dynamicBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    ExtraOptions
> = async (args, api, extraOptions) => {
    const orgBaseUrl = selectAuth(api.getState() as RootState).baseURL;
    // gracefully handle scenarios where data to generate the URL is missing
    if (!orgBaseUrl) {
        return {
            error: {
                status: 400,
                statusText: 'Bad Request',
                data: 'dynamic route not found',
            },
        }
    }

    const urlEnd = typeof args === 'string' ? args : args.url

    // construct a dynamically generated portion of the url
    const adjustedUrl = `${orgBaseUrl}{urlEnd}`
    const adjustedArgs = typeof args === 'string' ? { url: adjustedUrl } : { ...args, url: adjustedUrl };
    // Get the token from localStorage
    const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")!).token
        : null;
    // Set headers if token is available
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // Merge headers with existing headers
    const mergedHeaders = { ...headers, ...extraOptions?.headers };
    // provide the amended url and other params to the raw base query
    return rawBaseQuery({ ...adjustedArgs, headers: mergedHeaders }, api, extraOptions)

}

export const ClientAndProjectsApi = createApi({
    reducerPath: 'ClientAndProjectsApi',
    baseQuery:dynamicBaseQuery,
    // baseQuery: fetchBaseQuery({
    //     baseUrl: baseUrl.BASE_URL,
    //     prepareHeaders: (headers) => {
    //         // Get the token from localStorage
    //         const token = localStorage.getItem("user")
    //             ? JSON.parse(localStorage.getItem("user")!).token
    //             : null;
    //         if (token) {
    //             headers.set('Authorization', `Bearer ${token}`);
    //         }
    //         return headers;
    //     },
    // }),
    endpoints: (builder) => ({
        getAllClient: builder.query({
            query: ({ pageSize, pageNumber, sortBy, sortOrder, searchStr }) => {
                return {
                    url: `/GetAllClients?pageSize=${pageSize}&pageNumber=${pageNumber}&sortBy=${sortBy}&sortOrder=${sortOrder}&searchStr=${searchStr}`,
                    method: "GET",
                }
            },
        }),
        getClientById: builder.query({
            query: ({ clientId }) => {
                return {
                    url: `/GetClientsbyId?clientId=${clientId}`,
                    method: "GET",
                }
            },
        }),
        createClient: builder.mutation({
            query: (body) => {
                return {
                    url: `/CreateClient`,
                    method: "POST",
                    body,
                }
            },
        }),
        updateClient: builder.mutation({
            query: (body) => {
                return {
                    url: `/UpdateClients`,
                    method: "PUT",
                    body,
                }
            },
        }),

        getAllProjects: builder.query({
            query: ({ pageSize, pageNumber, sortBy, sortOrder, searchStr }) => {
                return {
                    url: `/GetAllProjects?pageSize=${pageSize}&pageNumber=${pageNumber}&sortBy=${sortBy}&sortOrder=${sortOrder}&searchStr=${searchStr}`,
                    method: "GET",
                }
            },
        }),
        getProjectById: builder.query({
            query: ({ projectId }) => {
                return {
                    url: `/GetProjectbyId?projectId=${projectId}`,
                    method: "GET",
                }
            },
        }),
        createProject: builder.mutation({
            query: (body) => {
                return {
                    url: `/CreateProject`,
                    method: "POST",
                    body,
                }
            },
        }),
        updateClientStatus: builder.mutation({
            query: ({ clientId, status }) => {
                return {
                    url: `/UpdateClientStatus?clientId=${clientId}&status=${status}`,
                    method: "PUT",
                    params: { clientId, status }
                }
            },
        }),
        getAllProjectLookUp: builder.query({
            query: () => {
                return {
                    url: `/GetAllProjectLookUp`,
                    method: "GET",
                }
            },
        }),
    }),
})


export const {

    useGetAllClientQuery,
    useGetClientByIdQuery,
    useCreateClientMutation,
    useUpdateClientMutation,
    useGetAllProjectsQuery,
    useGetProjectByIdQuery,
    useCreateProjectMutation,
    useUpdateClientStatusMutation,
    useGetAllProjectLookUpQuery

} = ClientAndProjectsApi